import {
	ChangeEvent,
	Dispatch,
	FunctionComponent,
	SetStateAction,
	useEffect,
	useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, MenuItem, TextField } from "@mui/material";
import { Stack } from "@mui/system";

import { Loader, Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, getAllUserRoles, renderErrorMessages } from "@/utils";
import { refusalText } from "@/utils/refusalText";
import { useGetRolesQuery, useInviteUserMutation } from "@/pages/Private/redux/admin/admin.api";
import { DEFAULT } from "@/constants";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { InviteForm, InviteFormSchema } from "../schema/invite-user";
import { InviteSendingUserField } from "./InviteSendingUserField";
import { rolesOfferedFor } from "./grantableRoles";
import { SendingSeatSlotData } from "./sendingSeatAvailability";

interface InviteUserModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	companyId?: number;
	/** Seat numbers from `useSlotsAvailableQuery`, already held by both callers. */
	slotData?: SendingSeatSlotData | null;
	isSequencerActive?: boolean;
}

export const InviteUserModal: FunctionComponent<InviteUserModalProps> = ({
	isOpen,
	setIsOpen,
	companyId,
	slotData,
	isSequencerActive,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`inviteUser.modal.${key}`);

	const { data: roles } = useGetRolesQuery();
	const [invite, { isLoading, error }] = useInviteUserMutation();

	const {
		control,
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<InviteForm>({
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			roles: [],
			companyId,
			// Non-sending by default: granting a seat is an opt-in the admin makes deliberately.
			linkedinAllowed: false,
		},
		resolver: zodResolver(InviteFormSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const {
		fields: userRoles,
		remove,
		append,
	} = useFieldArray({
		control,
		name: "roles",
	});

	// The roles the API would accept from this caller for this invitee: ADMIN (GLOBAL_ADMIN) only
	// for an staff-domain (@signalist.io, @digital-now.io, @signalist.de) address (PREC-4636), nothing above the caller's own
	// permissions (PREC-4634). Re-read as the email is typed; a pick no longer allowed is dropped.
	const email = watch("email");
	const callerRoles = useAppSelector(authSelector).user?.roles;
	const rolesOptions = useMemo(
		() => rolesOfferedFor(roles?.data ?? [], { email, viaInvitation: true }, callerRoles ?? []),
		[roles?.data, email, callerRoles]
	);
	const pickedRole = userRoles[0]?.identifier;

	useEffect(() => {
		if (pickedRole && !rolesOptions.some((role) => role.code === pickedRole)) {
			remove();
		}
	}, [pickedRole, rolesOptions, remove]);

	const onSubmit = async (values: InviteForm) => {
		const { roles: selectedRoles } = values;

		try {
			await invite({ ...values, roles: selectedRoles.map((role) => role.identifier) }).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleUserRoleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const selectedRole = roles?.data.find((role) => role.code === event.target.value);

		if (selectedRole) {
			remove();
			append(getAllUserRoles([selectedRole]));
		}
	};

	const handleUnselectRole = (code: string) => {
		const indexOfRemovedRole = userRoles.findIndex((role) => role.identifier === code);

		remove(indexOfRemovedRole);
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={ts("title")}
		>
			{!roles ? (
				<Loader />
			) : (
				<Stack spacing={2}>
					<TextField
						error={!!errors.firstName?.message}
						label={t("basics.firstName")}
						placeholder={t("basics.firstName")}
						{...register("firstName")}
					/>

					<TextField
						error={!!errors.lastName?.message}
						label={t("basics.lastName")}
						placeholder={t("basics.lastName")}
						{...register("lastName")}
					/>

					<TextField
						error={!!errors.email?.message}
						label={t("basics.email")}
						placeholder={t("basics.email")}
						{...register("email")}
					/>

					<TextField
						select
						error={!!errors.roles?.message}
						label={t("basics.role")}
						value={userRoles[0]?.identifier ?? DEFAULT}
						onChange={handleUserRoleChange}
					>
						<MenuItem disabled value={DEFAULT}>
							{ts("rolePlaceholder")}
						</MenuItem>

						{rolesOptions.map((role) => (
							<MenuItem key={role.id} value={role.code}>
								{role.name}
							</MenuItem>
						))}
					</TextField>

					<InviteSendingUserField
						isSequencerActive={isSequencerActive}
						slotData={slotData}
						value={watch("linkedinAllowed")}
						onChange={(next) => setValue("linkedinAllowed", next)}
					/>

					{userRoles.map((uR) => uR.name).includes("Admin") && (
						<Alert severity="error">
							This User will have access to all companies and their data. Only for Digital Now
							Employees.
						</Alert>
					)}

					{getAllErrors(undefined, formErrors).length
						? renderErrorMessages(getAllErrors(undefined, formErrors))
						: null}

					{/* The API's reason for a refused invite, not only "Something went wrong" (PREC-4636). */}
					{error ? (
						<Alert
							severity="error"
							sx={{ alignItems: "center", bgcolor: "error.light", fontSize: 14 }}
						>
							{refusalText(error)}
						</Alert>
					) : null}
				</Stack>
			)}
		</Modal>
	);
};
